import { html, property, query } from 'lit-element';
import {
  KatLitElement,
  Keys,
  register,
  event,
  EventEmitter,
} from '../../shared/base';
import baseStyles from '../../shared/base/base.lit.scss';
import styles from './tabs.lit.scss';

// NOTE: accessibility tips taken from https://inclusive-components.design/tabbed-interfaces/

/**
 * @component {kat-tabs} KatalTabs Use tabs to enable customers an easy way to switch between different sections of information while staying on the same view.
 * @guideline Do Ensure that tab-id properties of all child kat-tabs elements are unique.
 * @guideline Do Use tabs for organizing discrete blocks of information.
 * @guideline Do Use no more than seven tabs in a tab view to help maintain an uncluttered UI, and reduce cognitive load for customers.
 * @guideline Do All kat-tabs children must be direct children
 * @guideline Dont Don't use tabs as steps or navigation.
 * @guideline Dont Don’t hide error messages or critical information inside tabs.
 * @guideline Dont Don't use tabs to create flows.
 * @subcomponent ./tabs-tab.ts
 * @contentProvided
 * @example SampleTabs {"selected": "2", "content": "<kat-tab status=\"NEW\" tab-id=\"1\" label=\"tab1\">tab 1 content</kat-tab><kat-tab tab-id=\"2\" label=\"tab2\">tab 2 content</kat-tab>"}
 * @example Slots {"selected": "2", "content": "<kat-tab tab-id=\"1\"><span slot=\"label\"><kat-icon name=\"account_balance_wallet\" size=\"small\"></kat-icon>tab1</span><kat-icon slot=\"status\" name=\"ac_unit\" size=\"small\"></kat-icon>tab 1 content</kat-tab><kat-tab tab-id=\"2\"><span slot=\"label\">tab2</span>tab 2 content</kat-tab>"}
 * @status Production
 * @theme flo
 */
@register('kat-tabs')
export class KatTabs extends KatLitElement {
  /** The name of the tab currently being displayed. Defaults to showing the first tab. */
  @property()
  selected?: string;

  static get styles() {
    return [baseStyles, styles];
  }

  _selectedTab = null;
  _userInitiated = false;

  /** Fires when the selected tab has changed. The event detail contains "selected" which is the target tab to display. The event can be cancelled to prevent the tab from changing. */
  @event('change', { cancelable: true })
  private _change: EventEmitter<{ selected: string }>;

  @query('.root')
  _root: HTMLElement;

  constructor() {
    super();
    this._listeners = [
      {
        target: window,
        listeners: [['resize', this._handleResize.bind(this)]],
      },
      [
        'keydown',
        e => {
          // If event didn't originate from a kat-tab, ignore it
          if (e.target.tagName !== 'KAT-TAB') return;

          if (e.keyCode === Keys.ArrowLeft || e.keyCode === Keys.ArrowRight) {
            e.preventDefault();
            e.stopPropagation();
            const tab = this._selectedTab;
            const next = !tab
              ? this.children[0]
              : e.keyCode === Keys.ArrowLeft
              ? tab.previousElementSibling
              : tab.nextElementSibling;
            if (next) {
              this._setSelected(next.tabId);
            }
            return;
          }

          if (e.keyCode === Keys.ArrowDown) {
            // focus the content of the current tab
            e.preventDefault();
            e.stopPropagation();
            const tab = this._selectedTab;
            tab?.focusContent();
          }
        },
      ],
    ];
  }

  firstUpdated() {
    super.firstUpdated();
    this._selectTab();

    const observer = new MutationObserver(() => this._handleResize());
    observer.observe(this, { attributes: true });
  }

  updated(changed) {
    if (changed.has('selected')) {
      this._selectTab();
    }
  }

  _selectTab(e?: Event) {
    if (e) e.stopPropagation();

    let selectedTab;
    Array.from(this.children).forEach((tab, i) => {
      const selected =
        // auto-select the first tab if no selected id is set
        (i === 0 && !this.selected) ||
        // prevent multiple tabs from being selected at once
        (!selectedTab && tab.tabId === this.selected);
      tab.selected = selected;
      if (selected) {
        selectedTab = tab;
      }
    });

    this._selectedTab = selectedTab;
    if (!this.selected && selectedTab) {
      this.selected = selectedTab.tabId;
    }

    this._handleResize();
  }

  _setSelected(id) {
    const cancelled = !this._change.emit({ selected: id });
    if (cancelled) return;

    this._userInitiated = true;
    this.selected = id;
  }

  _handleResize(e?: Event) {
    if (e) e.stopPropagation();

    const tab = this._selectedTab;
    if (!tab || !tab._content) return;
    const updateMargin = () => {
      const style = getComputedStyle(tab._content);
      const height =
        tab._content.offsetHeight +
        styleToNumber(style.marginTop) +
        styleToNumber(style.marginBottom);
      this._root.style.marginBottom = `${height}px`;
    };
    updateMargin();
    // we do this again in a timeout so we can catch any height changes caused
    // by a child tabs element updating after we've already updated
    setTimeout(updateMargin, 10);
    if (this._userInitiated) {
      this._userInitiated = false;
      tab.focus();
    }
  }

  _handleSelected(e) {
    e.stopPropagation();
    this._setSelected(e.detail.id);
  }

  render() {
    return html`
      <div
        class="root"
        role="tablist"
        @select=${this._handleSelected}
        @_resize=${this._handleResize}
        @_invalidate=${this._selectTab}
      >
        <slot></slot>
      </div>
    `;
  }
}

function styleToNumber(prop: string) {
  return parseInt(prop.replace(/px$/, ''), 10);
}
